import React from 'react';
import { AppBar, Toolbar, Box, Typography, Button, IconButton, Avatar, Tooltip } from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import MenuBookIcon from '@mui/icons-material/MenuBook';
import DarkModeIcon from '@mui/icons-material/DarkMode';
import LightModeIcon from '@mui/icons-material/LightMode';
import LogoutIcon from '@mui/icons-material/Logout';
import { useNavigate } from 'react-router-dom';
import { BrandLogo } from '../BrandLogo';
import { getInitials } from '../../context/AuthContext';
import { LogoutConfirmDialog } from '../LogoutConfirmDialog';

export const WorkspaceHeader = ({ 
  categoryId, 
  isLight, 
  toggleTheme, 
  user, 
  logout,
  basePath
}) => {
  const navigate = useNavigate();
  const [logoutDialogOpen, setLogoutDialogOpen] = React.useState(false);

  return (
    <AppBar position="static" elevation={0} sx={{ borderBottom: '1px solid var(--border-main)', bgcolor: 'var(--bg-paper)', zIndex: 10 }}>
      <Toolbar sx={{ justifyContent: 'space-between', minHeight: '56px !important', px: { xs: 2, md: 3 } }}>
        <Box
          onClick={() => navigate('/interview-prep')}
          sx={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}
        >
          <BrandLogo size="medium" isLight={isLight} />
        </Box>

        <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center' }}>
          <Box
            onClick={() => navigate(basePath)}
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 0.75,
              color: '#71717a',
              cursor: 'pointer',
              px: 1.5,
              py: 0.5,
              borderRadius: 1.5,
              transition: 'all 0.15s',
              '&:hover': { color: '#e2e8f0', bgcolor: '#1a1a1e' }
            }}
          >
            <ArrowBackIcon sx={{ fontSize: 16 }} />
            <Typography variant="caption" fontWeight={700} sx={{ letterSpacing: '0.4px', textTransform: 'uppercase', fontSize: '0.7rem' }}>
              Back
            </Typography>
          </Box>
          <Box sx={{ width: '1px', height: '20px', bgcolor: '#1f1f22', mx: 0.5 }} />
          <Button
            color="inherit"
            startIcon={<MenuBookIcon sx={{ fontSize: 15 }} />}
            onClick={() => {
              const segs = basePath.split('/').filter(Boolean);
              if (segs.length >= 2) {
                // Navigate to /interview-prep/:trackId
                navigate(`/interview-prep/${segs[1]}`);
              } else {
                navigate('/interview-prep');
              }
            }}
            sx={{
              color: isLight ? '#64748b' : '#71717a',
              fontWeight: 700,
              fontSize: '0.72rem',
              textTransform: 'none',
              px: 1.5,
              py: 0.5,
              minHeight: 0,
              borderRadius: 1.5,
              '&:hover': { color: isLight ? '#0f172a' : '#e2e8f0', bgcolor: isLight ? 'rgba(0,0,0,0.05)' : '#1a1a1e' }
            }}
          >
            Library
          </Button>

          <Box sx={{ width: '1px', height: '20px', bgcolor: isLight ? 'rgba(0,0,0,0.1)' : '#1f1f22', mx: 0.5 }} />

          <Tooltip title={isLight ? "Switch to Dark Mode" : "Switch to Light Mode"} arrow>
            <IconButton onClick={toggleTheme} sx={{ color: isLight ? '#0f172a' : '#f1f5f9' }}>
              {isLight ? <DarkModeIcon /> : <LightModeIcon />}
            </IconButton>
          </Tooltip>

          {user ? (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <Avatar sx={{ width: 32, height: 32, bgcolor: 'primary.main', color: '#fff', fontSize: '0.85rem', fontWeight: 700, ml: 1 }}>
                {getInitials(user)}
              </Avatar>
              <Tooltip title="Logout" arrow>
                <IconButton onClick={() => setLogoutDialogOpen(true)} size="small" sx={{ color: isLight ? '#64748b' : '#a1a1aa' }}>
                  <LogoutIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Box>
          ) : (
            <Button
              variant="outlined"
              onClick={() => navigate(`/interview-prep/login?redirect=${encodeURIComponent(window.location.pathname)}`)}
              sx={{
                borderRadius: 2,
                textTransform: 'none',
                fontWeight: 700,
                ml: 1,
                borderColor: isLight ? 'rgba(0,0,0,0.1)' : 'rgba(255,255,255,0.1)',
                color: isLight ? '#0f172a' : '#f1f5f9',
                '&:hover': { borderColor: 'primary.main', bgcolor: 'transparent' }
              }}
            >
              Log In
            </Button>
          )}
        </Box>
      </Toolbar>
      <LogoutConfirmDialog 
        open={logoutDialogOpen} 
        onClose={() => setLogoutDialogOpen(false)} 
        onConfirm={() => {
          setLogoutDialogOpen(false);
          logout();
        }} 
      />
    </AppBar>
  );
};
